/**
 * Save / Save-as dialog for a data view. Project-visibility + default are gated
 * to admins (matching the server trigger); others save private views.
 */
import { useState, useEffect } from 'react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils/cn'
import { Lock, Users2 } from 'lucide-react'
import type { ViewVisibility } from '../types/data-view-spec'

export interface SaveViewPayload {
  name: string
  description: string
  visibility: ViewVisibility
  isDefault: boolean
}

interface SaveViewDialogProps {
  open: boolean
  onOpenChange: (o: boolean) => void
  defaultName: string
  canManage: boolean
  saving?: boolean
  onSave: (p: SaveViewPayload) => void
}

export function SaveViewDialog({
  open, onOpenChange, defaultName, canManage, saving, onSave,
}: SaveViewDialogProps) {
  const [name, setName] = useState(defaultName)
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState<ViewVisibility>('private')
  const [isDefault, setIsDefault] = useState(false)

  useEffect(() => {
    if (open) {
      setName(defaultName); setDescription(''); setVisibility('private'); setIsDefault(false)
    }
  }, [open, defaultName])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>ذخیرهٔ نما</DialogTitle>
          <DialogDescription>این پیکربندی ستون‌ها، فیلترها و مرتب‌سازی را به‌عنوان یک نما ذخیره کنید.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="block text-sm">
            <span className="text-muted-foreground">نام نما</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" autoFocus />
          </label>
          <label className="block text-sm">
            <span className="text-muted-foreground">توضیح (اختیاری)</span>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <VisCard
              active={visibility === 'private'} onClick={() => setVisibility('private')}
              icon={Lock} title="خصوصی" desc="فقط شما"
            />
            <VisCard
              active={visibility === 'project'} onClick={() => canManage && setVisibility('project')}
              icon={Users2} title="اشتراکی" desc={canManage ? 'همهٔ اعضا' : 'فقط مدیر'} disabled={!canManage}
            />
          </div>

          {canManage && visibility === 'project' && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={isDefault} onCheckedChange={(v) => setIsDefault(!!v)} />
              <span>تنظیم به‌عنوان نمای پیش‌فرض این فرم</span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>انصراف</Button>
          <Button disabled={!name.trim() || saving} onClick={() => onSave({ name: name.trim(), description, visibility, isDefault })}>
            {saving ? 'در حال ذخیره…' : 'ذخیره'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function VisCard({
  active, onClick, icon: Icon, title, desc, disabled,
}: { active: boolean; onClick: () => void; icon: typeof Lock; title: string; desc: string; disabled?: boolean }) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      className={cn(
        'flex flex-col items-start gap-0.5 rounded-md border p-2.5 text-start transition-colors',
        active ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <span className="inline-flex items-center gap-1.5 text-sm font-medium"><Icon className="w-3.5 h-3.5" />{title}</span>
      <span className="text-[11px] text-muted-foreground">{desc}</span>
    </button>
  )
}
